import { useState } from "react";
import { Upload, X, Film } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { parseVideoUrl } from "../../lib/video";

// Video uploader (mp4/webm/mov, max 25 MB) + paste-URL field (YouTube / Vimeo / direct).
export default function VideoUpload({ label, value, onChange, hint, testId }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Video too large (max 25 MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-video", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  const parsed = parseVideoUrl(value);

  return (
    <div className="mb-5">
      <label className="block text-xs uppercase tracking-widest text-[#D8D6D0]/60 mb-2">
        {label}
      </label>

      {value ? (
        <div className="relative group" data-testid={testId || "video-upload-preview"}>
          <div className="aspect-video overflow-hidden bg-[#0d0d0d] border border-[#D8D6D0]/15">
            {parsed?.kind === "iframe" ? (
              <iframe
                title="Video preview"
                src={parsed.embed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            ) : parsed?.kind === "file" ? (
              <video src={parsed.src} controls className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/50 text-sm p-4 text-center">
                Unrecognised video URL. YouTube, Vimeo or direct .mp4/.webm only.
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="video-clear-btn"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-1.5 bg-[#151515]/90 border border-[#D8D6D0]/20 hover:bg-[#8C1D30] hover:border-[#8C1D30] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label
          data-testid={testId || "video-upload-dropzone"}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-3 aspect-video border-2 border-dashed cursor-pointer transition-colors ${
            dragOver
              ? "border-[#8C1D30] bg-[#8C1D30]/5"
              : "border-[#D8D6D0]/15 hover:border-[#D8D6D0]/40 bg-[#0d0d0d]"
          }`}
        >
          {uploading ? (
            <>
              <Upload className="w-7 h-7 text-[#D8D6D0]/40 animate-pulse" />
              <span className="text-sm text-[#D8D6D0]/60">Uploading…</span>
            </>
          ) : (
            <>
              <Film className="w-7 h-7 text-[#D8D6D0]/30" />
              <div className="text-center px-4">
                <div className="text-sm text-[#D8D6D0]/70">Drop video or click to upload</div>
                <div className="text-[11px] text-[#D8D6D0]/40 mt-1">MP4 · WebM · MOV · max 25 MB</div>
              </div>
            </>
          )}
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      )}

      <div className="mt-3">
        <input
          type="text"
          data-testid="video-url-input"
          placeholder="…or paste YouTube / Vimeo / direct .mp4 URL"
          value={value && !value.startsWith("/api/") ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#D8D6D0]/15 px-3 py-2 text-xs text-[#F5F3EF]/80 placeholder-[#D8D6D0]/30 focus:outline-none focus:border-[#8C1D30]"
        />
      </div>

      {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1.5">{hint}</p>}
    </div>
  );
}
